import React, { Fragment, useEffect, useState } from "react";
import {
BaseTable,
Button,
Card,
HeaderMenu,
Input,
ActionButton, Loading, Modal,
TableHeader,
HintList
} from "components";
import * as yup from "yup"
import { TbChevronRight } from "react-icons/tb";
import {
useApi,
useToggle,
} from "utils";
import { useShiftList } from "./utils/useShiftList";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import moment from "moment";

export const dataContext = React.createContext({
    currentPage: 1,
    limit: 10,
    size: 1,
    total: 0,
});

export default function Shift() {
    const { data, isLoading, refetch } = useShiftList();
    const [editData, setEditData] = useState<CreateData | null>(null);
    const { isActive, toggle:toggleIsactive } = useToggle(false);
    const modalDelete = useToggle(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    let number = 1;

    return (
        <section className="container pt-5  gap-5 flex flex-col">
            <HeaderMenu title="SHIFT"  className="sticky top-[-25px] z-[2]"/>

            <Card>
                <Card.Body className="flex-row justify-end items-center sticky top-[28px] z-[2] bg-white">
                    <Button color="primary" onClick={()=>{
                        setEditData(null);
                        toggleIsactive()
                    }}>
                        CREATE NEW <TbChevronRight />
                    </Button>
                </Card.Body>

                <Card.Body>
                    
                    <BaseTable>
                        <TableHeader>
                            <tr>
                                <th>NO</th>
                                <th>SHIFT</th>
                                <th>IN TIME</th>
                                <th>OUT TIME</th>
                                <th>DESCRIPTION</th>
                                <th className="w-[150px]">ACTION</th>
                            </tr>
                        </TableHeader>
                        <tbody>
                        {
                            isLoading ? (
                                <Loading loading={isLoading}/>
                            ) : ( 
                                    data.map((item,index) => {
                                        return(
                                            <tr key={index}>
                                                <td>
                                                {number++} 
                                                </td>
                                                <td>{item.mr_shift_name}</td>
                                                <td>{item.in_time ? moment(item.in_time, "HH:mm:ss").format("HH:mm"):''}</td>
                                                <td>{item.out_time ? moment(item.out_time, "HH:mm:ss").format("HH:mm"):''}</td>
                                                <td>{item.description}</td> 
                                                <td className="text-center flex justify-center">
                                                    <ActionButton.Update permission="SR014" onClick={()=>{
                                                        setEditData({
                                                            mr_shift_id: item.mr_shift_id,
                                                            mr_shift_name: item.mr_shift_name,
                                                            description: item.description,
                                                            in_time: item.in_time,
                                                            out_time: item.out_time,
                                                        });
                                                        toggleIsactive()
                                                    }} />
                                                    
                                                    <ActionButton.Delete permission="SR014"  onClick={()=>{
                                                        setSelectedItem({
                                                            mr_shift_id: item.mr_shift_id,
                                                            mr_shift_name: item.mr_shift_name,
                                                            description: item.description,
                                                            in_time: item.in_time,
                                                            out_time: item.out_time,
                                                        });
                                                        modalDelete.toggle()
                                                    }} />
                                                
                                                </td>
                                            </tr>
                                        )
                                    })
                                )    
                        }
                        </tbody>
                    </BaseTable>
                </Card.Body>
            </Card>

            {isActive && (
                <CreateModal
                    toggle={toggleIsactive}
                    refetch={refetch}
                    mode={editData ? "edit" : "create"}
                    initialData={editData ?? undefined}
                />
            )}

            {modalDelete.isActive && (
                <ModalDelete
                    data={selectedItem}
                    modalState={modalDelete}
                    onSuccess={refetch}
                />
            )}

        </section>
    );

}


interface CreateModalProps{
    toggle: () => void
    refetch: () => void
    mode: "create" | "edit";
    initialData?: CreateData;
}
interface CreateData{
    mr_shift_id?: number;
    mr_shift_name:string;
    description:string;
    in_time?: string | null | undefined;
    out_time?: string | null | undefined;
}


function CreateModal({
    toggle,
    refetch,
    mode,
    initialData,
}: CreateModalProps) {
    const api = useApi();
  
    const defaultValues: CreateData = {
        mr_shift_id: initialData?.mr_shift_id,
        mr_shift_name: initialData?.mr_shift_name ?? "",
        description: initialData?.description ?? "",
        in_time: initialData?.in_time ?? null,
        out_time: initialData?.out_time ?? null,
    };
    const validationSchema = yup.object().shape({
        mr_shift_name: yup.string().label("Shift Name").required(),
        description: yup.string().label("Description").required(),
        in_time:yup.string().nullable(),
        out_time:yup.string().nullable(),
    })

    const methods = useForm<CreateData>({
        defaultValues,
        resolver: yupResolver(validationSchema),
    });
    const {control, reset} = methods

    const onSubmit = (value: CreateData) => {
        return new Promise<void>((resolve) => {
          toast.promise(
            api.post("/shift/save_data_shift", value),
            {
              loading: "Loading...",
              success: (res) => res.data.message,
              error: (err) => err.response.data.message ?? err.response.message
            }
          ).then(() => {
            toggle()
            refetch()
          }).catch(() => {}).finally(resolve)
        })
      }

    useEffect(() => {
        if (mode === "create") {
            reset({
                mr_shift_id: undefined,
                mr_shift_name: "",
                description: "",
                in_time: null,
                out_time: null,
            });
        }
    
        if (mode === "edit" && initialData) {
            reset(initialData);
        }
    }, [mode, initialData, reset]);
    
    return(
        <Modal isOpen title={mode === "edit" ? "EDIT SHIFT" : "CREATE SHIFT"} size="md" closeModal={toggle}>
        
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Modal.Body>
                <div className="flex lg:grid-cols-2 gap-[50px] justify-between">
                    <div className="w-full">
                        <div className="">SHIFT NAME</div>
                        <div className="">
                            <Controller
                                name="mr_shift_name"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Input
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>

                        <div className="mt-4">DESCRIPTION</div>
                        <div className="">
                            <Controller
                                name="description"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Input
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>

                        <div className="mt-4">IN TIME</div>
                        <div className="">
                            <Controller
                                name="in_time"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Input
                                        type="time"
                                        value={field.value??''}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>

                        <div className="mt-4">OUT TIME</div>
                        <div className="">
                            <Controller
                                name="out_time"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Input
                                        type="time"
                                        value={field.value ?? ''}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="w-full">

                        <HintList
                            title=""
                            list={[
                                
                                {
                                label: "SHIFT NAME",
                                list: [
                                    {
                                    label: "Definition",
                                    value: ""
                                    },
                                    {
                                    label: "Purpose",
                                    value: ""
                                    },
                                    {
                                    label: "Example",
                                    value: "Morning"
                                    },
                                ]
                                },
                                {
                                    label: "DESCRIPTION",
                                    list: [
                                        {
                                            label: "Definition",
                                            value: ""
                                        },
                                        {
                                            label: "Purpose",
                                            value: ""
                                        },
                                        {
                                            label: "Example",
                                            value: "Morning shift"
                                        },
                                    ]
                                },
                                {
                                    label: "IN TIME",
                                    list: [
                                        {
                                            label: "Definition",
                                            value: ""
                                        },
                                        {
                                            label: "Purpose",
                                            value: ""
                                        },
                                        {
                                            label: "Example",
                                            value: "10.00"
                                        },
                                    ]
                                },
                                {
                                    label: "OUT TIME",
                                    list: [
                                        {
                                        label: "Definition",
                                        value: ""
                                        },
                                        {
                                        label: "Purpose",
                                        value: ""
                                        },
                                        {
                                        label: "Example",
                                        value: "21.00"
                                        },
                                    ]
                                },
                            ]}
                        />
                    </div>
                </div>
            </Modal.Body>
  
            <Modal.Footer className="text-end">
              <Button type="submit" color="primary" loading={methods.formState.isSubmitting ? "true" : undefined}>Save</Button>
            </Modal.Footer>
          </form>
       
      </Modal>
    )
}


type ModalDeleteProps = {
    data: Pick<
      CreateData,
        | "mr_shift_id"
        | "mr_shift_name"
        | "in_time"
        | "out_time"
    >
    modalState: ReturnType<typeof useToggle>
    onSuccess?: () => void
  }
  
  export const ModalDelete = ({
    data,
    modalState,
    onSuccess
  }: ModalDeleteProps) => {
    const [loadingData, setLoadingData] = useState(false)
    const api = useApi()
    
  
    const handleDeleteData = async () => {
      setLoadingData(true)
      await toast
        .promise(api.delete(`/shift/${data.mr_shift_id}`), {
          loading: "Loading...",
          success: (res) => res?.data?.message,
          error: (err) => err?.response?.data?.message
        })
        .then(() => {
          modalState.setActive(false)
          onSuccess?.()
        })
        .finally(() => {
          setLoadingData(false)
        })
    }
  
    return (
      <Modal
        isOpen={modalState.isActive}
        title="DELETE SHIFT"
        size="xl"
        closeModal={modalState.toggle}>
        <Modal.Body>
          <Fragment>
            <small className="text-lg font-medium text-red-400">
              Are you sure want to delete this data?
            </small>
  
            <Input disabled label="SHIFT NAME#" defaultValue={data.mr_shift_name} />
            <Input disabled label="IN TIME" defaultValue={data.in_time??''} />
            <Input disabled label="OUT TIME" defaultValue={data.out_time??''} />
          </Fragment>
        </Modal.Body>
  
        <Modal.Footer>
          <Button
            type="button"
            color="error"
            disabled={loadingData}
            loading={loadingData ? "true" : undefined}
            onClick={handleDeleteData}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
  


